/**
 * SubscriptionAlert Component
 * Alert banners for subscription status warnings
 *
 * Shows alerts for:
 * - Past due payments
 * - Trial ending soon (<3 days)
 * - SKU limit warnings (>90%)
 * - Grace period warnings
 *
 * Per Inara's feedback:
 * - Dismissible for 24h (except trial <3 days)
 * - Trial <3 days is NOT dismissible
 */

import { useState, useEffect } from 'react'
import { Alert, Button, Space } from 'antd'
import { useStripeCheckout } from '../../hooks/useStripeCheckout'
import type { CurrentPlan, UsageMetrics } from '../../types/billing.types'

/** Duration in ms that alert dismissal persists (24 hours) */
const DISMISSAL_DURATION_MS = 24 * 60 * 60 * 1000

interface Props {
  currentPlan: CurrentPlan
  usage?: UsageMetrics
}

export function SubscriptionAlert({ currentPlan, usage }: Props) {
  const { openPortal, loading } = useStripeCheckout()
  const [dismissed, setDismissed] = useState(false)

  // Check localStorage for dismissed alerts (24h expiry)
  // Note: Synchronous localStorage read - no cleanup needed
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('billing-alert-dismissed')
    if (dismissedUntil) {
      const expiryTime = parseInt(dismissedUntil, 10)
      if (Date.now() < expiryTime) {
        setDismissed(true)
      } else {
        localStorage.removeItem('billing-alert-dismissed')
      }
    }
  }, [])

  const handleDismiss = () => {
    const expiryTime = Date.now() + DISMISSAL_DURATION_MS
    localStorage.setItem('billing-alert-dismissed', expiryTime.toString())
    setDismissed(true)
  }

  // Don't show if dismissed
  if (dismissed) {
    return null
  }

  // Past due payment
  if (currentPlan.status === 'past_due') {
    return (
      <Alert
        type="warning"
        message="Payment Failed - Update payment method"
        description={
          <span>
            Your subscription is past due. Service continues for 7 days. Update payment to avoid interruption.
          </span>
        }
        showIcon
        action={
          <Space direction="vertical">
            <Button type="primary" size="small" onClick={openPortal} loading={loading}>
              Update Payment Method
            </Button>
            <Button size="small" onClick={handleDismiss}>
              Dismiss for 24 hours
            </Button>
          </Space>
        }
        closable={false}
      />
    )
  }

  // Unpaid/suspended
  if (currentPlan.status === 'unpaid') {
    return (
      <Alert
        type="error"
        message="Service Suspended - Payment Required"
        description="Your subscription has been suspended due to payment failure. Update payment to restore access."
        showIcon
        action={
          <Button type="primary" danger size="small" onClick={openPortal} loading={loading}>
            Update Payment Method
          </Button>
        }
        closable={false}
      />
    )
  }

  // Trial ending soon (calculate days remaining)
  if (currentPlan.status === 'trialing' && currentPlan.trialEnd) {
    const daysRemaining = Math.ceil((currentPlan.trialEnd * 1000 - Date.now()) / (1000 * 60 * 60 * 24))

    if (daysRemaining <= 7) {
      const isDismissible = daysRemaining >= 3

      return (
        <Alert
          type={daysRemaining <= 3 ? 'warning' : 'info'}
          message={`Trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`}
          description={
            daysRemaining <= 3
              ? `Your trial ends soon. Add a payment method to continue using ${currentPlan.tier} features.`
              : `Your trial is ending soon. You'll be charged on the trial end date if you continue.`
          }
          showIcon
          action={
            isDismissible ? (
              <Button size="small" onClick={handleDismiss}>
                Dismiss for 24 hours
              </Button>
            ) : null
          }
          closable={false}
        />
      )
    }
  }

  // SKU limit warning (>90%)
  if (usage && usage.skuCount.percentage > 90) {
    const isAtLimit = usage.skuCount.current >= usage.skuCount.limit

    return (
      <Alert
        type={isAtLimit ? 'error' : 'warning'}
        message={
          isAtLimit
            ? `SKU Limit Reached (${usage.skuCount.current.toLocaleString()} / ${usage.skuCount.limit.toLocaleString()})`
            : `Approaching SKU Limit (${Math.round(usage.skuCount.percentage)}%)`
        }
        description={
          isAtLimit
            ? 'You\'ve reached your SKU limit. Upgrade to add more products.'
            : `You're using ${usage.skuCount.current.toLocaleString()} of ${usage.skuCount.limit.toLocaleString()} SKUs. Upgrade soon to avoid hitting the limit.`
        }
        showIcon
        action={
          <Space direction="vertical">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                // Scroll to upgrade options
                document.getElementById('upgrade-options')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              Upgrade Plan
            </Button>
            {!isAtLimit && (
              <Button size="small" onClick={handleDismiss}>
                Dismiss for 24 hours
              </Button>
            )}
          </Space>
        }
        closable={false}
      />
    )
  }

  // Grace period warning
  if (usage && usage.skuCount.inGracePeriod && usage.skuCount.graceUntil && usage.skuCount.targetValue) {
    const daysRemaining = Math.ceil((usage.skuCount.graceUntil * 1000 - Date.now()) / (1000 * 60 * 60 * 24))

    return (
      <Alert
        type="warning"
        message="Grace Period Active - Reduce SKU Count"
        description={
          <span>
            You have {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} to reduce your SKU count from{' '}
            {usage.skuCount.current.toLocaleString()} to {usage.skuCount.targetValue.toLocaleString()} or upgrade your plan.
          </span>
        }
        showIcon
        action={
          <Button
            type="primary"
            size="small"
            onClick={() => {
              document.getElementById('upgrade-options')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            Upgrade Plan
          </Button>
        }
        closable={false}
      />
    )
  }

  return null
}
